/** @jsxImportSource react */
import { useState } from 'react'
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, List, ListItem, ListItemText, Stack, Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { entityLabelPlural } from '../../utils/helpers'
import { FIELD_TYPE_LABELS, contentsSummary, templateIcon } from './templateModel'

const Section = ({ title, children }) => (
  <Box sx={{ mt: 3 }}>
    <Typography variant="overline" color="text.secondary">{title}</Typography>
    {children}
  </Box>
)

const fieldDetail = (f) => {
  if (f.links) return `${FIELD_TYPE_LABELS.LOOKUP} → ${entityLabelPlural(f.links)}`
  if (f.options.length) return `${FIELD_TYPE_LABELS[f.fieldType]}: ${f.options.join(', ')}`
  return FIELD_TYPE_LABELS[f.fieldType] || f.fieldType
}

const ItemList = ({ items, showType }) => (
  <List dense disablePadding>
    {items.map((it) => (
      <ListItem key={`${it.entityType}-${it.name}`} disableGutters>
        <ListItemText primary={showType && it.entityType ? `${it.name} · ${entityLabelPlural(it.entityType)}` : it.name} secondary={it.detail} />
      </ListItem>
    ))}
  </List>
)

/**
 * What a template sets up (record types and their fields, automations, roles, views, dashboards)
 * and the install button. Installing is blocked while the workspace has clashing forms.
 */
const TemplateDialog = ({ template: t, open, busy, onInstall, onClose }) => {
  const [sample, setSample] = useState(true)
  if (!t) return null
  const Icon = templateIcon(t.icon)
  const blocked = t.conflicts.length > 0

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
        <Icon color="primary" />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" component="div">{t.name}</Typography>
          <Typography variant="body2" color="text.secondary">{t.industry}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2">{t.description}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{contentsSummary(t)}</Typography>
        {blocked && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            This workspace already has some of what this template creates, so it can’t be installed here: {t.conflicts.join('; ')}.
          </Alert>
        )}

        <Section title="Record types">
          {t.recordTypes.map((rt) => (
            <Accordion key={rt.entityType} variant="outlined" disableGutters sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2">{entityLabelPlural(rt.entityType)} <Typography component="span" variant="body2" color="text.secondary">· {rt.fields.length} fields</Typography></Typography>
                  {rt.description && <Typography variant="body2" color="text.secondary">{rt.description}</Typography>}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                {rt.fields.map((f) => (
                  <Box key={f.label} sx={{ display: 'flex', gap: 1, py: 0.5, borderTop: 1, borderColor: 'divider', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                    <Typography variant="body2" sx={{ width: { xs: '100%', sm: 200 }, flexShrink: 0, fontWeight: 500 }}>
                      {f.label}{f.required && <Typography component="span" color="error.main"> *</Typography>}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0 }}>{fieldDetail(f)}</Typography>
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </Section>
        {t.automations.length > 0 && <Section title="Automations"><ItemList items={t.automations} /></Section>}
        {t.roles.length > 0 && <Section title="Roles"><ItemList items={t.roles} /></Section>}
        {(t.views.length > 0 || t.dashboards.length > 0) && (
          <Section title="Views & dashboards">
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
              {t.views.map((v) => <Chip key={`${v.entityType}-${v.name}`} label={`${v.name} (${entityLabelPlural(v.entityType)}, ${v.detail.toLowerCase()})`} />)}
              {t.dashboards.map((d) => <Chip key={d.name} label={`${d.name} dashboard`} />)}
            </Stack>
          </Section>
        )}
        <Alert severity="info" sx={{ mt: 3 }}>
          Everything is yours to change after installing: edit the forms, rules, roles, views and dashboards like any you created yourself.
        </Alert>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, py: 1.5 }}>
        {t.sampleRecords > 0 && (
          <FormControlLabel sx={{ mr: 'auto' }} disabled={blocked}
            control={<Checkbox checked={sample} onChange={(e) => setSample(e.target.checked)} />}
            label={`Add ${t.sampleRecords} example records (you can remove them later)`} />
        )}
        <Button onClick={onClose} disabled={busy}>Close</Button>
        <Button variant="contained" disabled={blocked || busy} onClick={() => onInstall(t, sample && t.sampleRecords > 0)}>
          {busy ? 'Installing…' : 'Install template'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default TemplateDialog
